import { HugeiconsIcon } from '@hugeicons/react'
import { UndoIcon } from '@hugeicons/core-solid-rounded'
import { useMemo } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'

import {
  CornerDotType,
  CornerSquareType,
  DotType,
  ShapeType
} from 'qr-code-styling'

import ColorPicker from '@/components/ui/ColorPicker'
import { useBranding } from '@/features/branding'
import { Styling } from '../hooks/useQrCode'
import Dropdown from '@/components/ui/Dropdown'
import Checkbox from '@/components/ui/Checkbox'
import Field from '@/components/ui/Field'

function useShapeOptions() {
  const { t } = useLingui()

  return useMemo(() => {
    return [
      {
        label: t`Square`,
        value: 'square'
      },
      {
        label: t`Circle`,
        value: 'circle'
      }
    ]
  }, [t])
}

function useDotTypeOptions() {
  const { t } = useLingui()

  return useMemo(() => {
    return [
      {
        label: t`Classy`,
        value: 'classy'
      },
      {
        label: t`Dots`,
        value: 'dots'
      },
      {
        label: t`Square`,
        value: 'square'
      },
      {
        label: t`Rounded`,
        value: 'rounded'
      }
    ]
  }, [t])
}

function useCornerDotTypeOptions() {
  const { t } = useLingui()

  return useMemo(() => {
    return [
      {
        label: t`Classy`,
        value: 'classy'
      },
      {
        label: t`Dot`,
        value: 'dot'
      },
      {
        label: t`Square`,
        value: 'square'
      }
    ]
  }, [t])
}

function useCornerSquareTypeOptions() {
  const { t } = useLingui()

  return useMemo(() => {
    return [
      {
        label: t`Classy`,
        value: 'classy'
      },
      {
        label: t`Dot`,
        value: 'dot'
      },
      {
        label: t`Square`,
        value: 'square'
      }
    ]
  }, [t])
}

export function StylingForm({
  value,
  onChange
}: {
  value: Styling
  onChange: (newStyling: Styling) => void
}) {
  const { t } = useLingui()
  const { branding } = useBranding()

  const shapeOptions = useShapeOptions()
  const dotTypeOptions = useDotTypeOptions()
  const cornerSquareTypeOptions = useCornerSquareTypeOptions()
  const cornerDotTypeOptions = useCornerDotTypeOptions()

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-heading3 text-text">
        <Trans>QR Style</Trans>
      </h2>
      <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
        <div>
          <Field label={t`Shape`} size="small">
            <Dropdown
              options={shapeOptions}
              value={value.shape}
              onChange={shape =>
                onChange({ ...value, shape: shape as ShapeType })
              }
            />
          </Field>
        </div>
        <div>
          <Field label={t`Dot`} size="small">
            <Dropdown
              options={dotTypeOptions}
              value={value.dotType}
              onChange={dotType =>
                onChange({
                  ...value,
                  dotType: dotType as DotType
                })
              }
            />
          </Field>
        </div>
        <div>
          <Field label={t`Corners`} size="small">
            <Dropdown
              options={cornerSquareTypeOptions}
              value={value.cornerSquareType}
              onChange={cornerSquareType =>
                onChange({
                  ...value,
                  cornerSquareType: cornerSquareType as CornerSquareType
                })
              }
            />
          </Field>
        </div>
        <div>
          <Field label={t`Corner Dots`} size="small">
            <Dropdown
              options={cornerDotTypeOptions}
              value={value.cornerDotType}
              onChange={cornerDotType =>
                onChange({
                  ...value,
                  cornerDotType: cornerDotType as CornerDotType
                })
              }
            />
          </Field>
        </div>
        <div>
          <Field label={t`Color`} size="small">
            <div className="flex flex-row items-center gap-1">
              <ColorPicker
                value={value.color}
                onChange={event =>
                  onChange({ ...value, color: event.target.value })
                }
              />
              {value.color !== branding.colors.primary && (
                <button
                  tabIndex={0}
                  onClick={() =>
                    onChange({ ...value, color: branding.colors.primary })
                  }
                >
                  <HugeiconsIcon icon={UndoIcon} className="size-5" />
                </button>
              )}
            </div>
          </Field>
        </div>
        <div>
          <Field label={t`With Logo?`} size="small">
            <div className="flex flex-row items-center py-1.5">
              <Checkbox
                checked={value.withLogo}
                onChange={() =>
                  onChange({ ...value, withLogo: !value.withLogo })
                }
              />
            </div>
          </Field>
        </div>
        <div>
          <Field label={t`With Text? (when supported)`} size="small">
            <div className="flex flex-row items-center py-1.5">
              <Checkbox
                checked={value.withText}
                onChange={() =>
                  onChange({ ...value, withText: !value.withText })
                }
              />
            </div>
          </Field>
        </div>
      </div>
    </div>
  )
}
